import type { ModuleDefinition, ModuleGroup, ModuleId, ModuleSection } from './types';

/**
 * The catalogue of modules this install ships — and the single navigation model that
 * drives the drawer, the breadcrumbs and routing
 * (docs/architecture/navigation-and-breadcrumbs.md §3).
 *
 * Wording comes from the published artifacts and is VERBATIM:
 *   * `name`        — `_assessment/06-dashboard/index.html`, the `APPS` array.
 *   * `navLabel`    — `packages/ui-kit/navigation-demo.html`, the `short` map.
 *   * `sections`    — `packages/ui-kit/navigation-demo.html`, each app's `sub` array.
 *
 * Do not shorten, re-word or re-order any of it. Entries that post-date the
 * artifacts are marked at the entry itself.
 *
 * LICENSABLE and NAVIGABLE are different things, and this file carries both:
 *
 *   * Every module the platform can SELL is listed here, so it appears on Talogent's
 *     Entitlements & Licensing screen and can be given seats before a line of its UI
 *     exists. A client buys a module ahead of delivery; the commercial catalogue
 *     cannot wait for the screens.
 *   * A module with an EMPTY `sections` array does not enter the drawer. Unbuilt
 *     modules show on the launcher as "Soon" tiles that do not open, rather than
 *     drilling into a menu of dead ends.
 *
 * So: give a module its sections when its screens land, and it becomes navigable.
 * Until then it is bought, visible and inert.
 *
 * `icon` is a logical name resolved to a Material icon inside ui-kit, so this package
 * stays free of any UI dependency and can run on the server.
 */

function keyOf(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Builds section routes so `/{moduleId}/{key}` is derived, never hand-written. */
function sections(
  moduleId: string,
  labels: readonly string[],
  icons: Readonly<Record<string, string>> = {},
): ModuleSection[] {
  return labels.map((label) => {
    const key = keyOf(label);
    const icon = icons[label];
    return { key, label, path: `/${moduleId}/${key}`, ...(icon ? { icon } : {}) };
  });
}

/**
 * A bucket of screens inside a module: one drawer heading, one URL level.
 * `/{moduleId}/{groupKey}` lists them; `/{moduleId}/{groupKey}/{sectionKey}` opens one.
 */
function group(
  moduleId: string,
  label: string,
  childLabels: readonly string[],
  icon?: string,
  childIcons: Readonly<Record<string, string>> = {},
): ModuleSection {
  const key = keyOf(label);
  return {
    key,
    label,
    path: `/${moduleId}/${key}`,
    ...(icon ? { icon } : {}),
    sections: sections(`${moduleId}/${key}`, childLabels, childIcons),
  };
}

/**
 * A bucket whose children are themselves buckets, or a mix of buckets and leaves.
 *
 * `group` above nests exactly one level, which covers every bucket APR had until
 * Application Evaluation: that one holds three sub-buckets (APR Model & Execution,
 * Initial Disposition, Disposition Intelligence) AND two plain leaves (Final Disposition,
 * Audit Trail) side by side, so its children cannot all be built by one helper.
 *
 * Children are passed already-built and their paths are REWRITTEN here, under this
 * bucket's key — the same rule `group` follows, so a path is still derived from position
 * in the tree and never hand-written. `resolveNavigation` walks the tree segment by
 * segment and the drawer renders it recursively, so depth is a property of the catalogue
 * alone; nothing downstream needs to know this bucket is deeper than its neighbours.
 */
function nest(
  moduleId: string,
  label: string,
  children: readonly ModuleSection[],
  icon?: string,
): ModuleSection {
  const key = keyOf(label);
  const base = `/${moduleId}/${key}`;
  const rebase = (s: ModuleSection, parent: string): ModuleSection => {
    const path = `${parent}/${s.key}`;
    return {
      ...s,
      path,
      ...(s.sections ? { sections: s.sections.map((c) => rebase(c, path)) } : {}),
    };
  };
  return {
    key,
    label,
    path: base,
    ...(icon ? { icon } : {}),
    sections: children.map((c) => rebase(c, base)),
  };
}

/** Marks the section that starts the drawer's bottom group. */
function startsBottom(section: ModuleSection): ModuleSection {
  return { ...section, startsBottomGroup: true };
}

/** Sold and visible, but with no screens yet — so no sections, so not in the drawer. */
const NO_SECTIONS: ModuleSection[] = [];

export const MODULE_CATALOG: readonly ModuleDefinition[] = [
  {
    id: 'apr',
    name: 'Application Portfolio Rationalization',
    navLabel: 'App Rationalization',
    group: 'applications',
    // Licence optimization is named explicitly: License Intelligence is a whole module
    // of this product, and a reader of the tile could otherwise assume rationalization
    // stops at retire/replatform decisions and misses the renegotiation workstream.
    description:
      'Rationalize, dispose and modernize the application estate, and optimize licensing spend.',
    icon: 'apr',
    path: '/apr',
    status: 'available',
    // Applications and Cost were briefly a module of their own. On the owner's
    // instruction they sit here instead, in a Master Data bucket: they read the `apr`
    // schema and describe the estate this module rationalizes, so they belong to the
    // application rather than standing beside it. More buckets join this one as APR's
    // own screens land.
    // Icons verbatim from APR's navConfig.ts: the Master Data hub is `storage`, its
    // Application Master Data leaf `inventory_2`, its Current Run Rate leaf
    // `attach_money`.
    sections: [
      // ORDER IS THE SOURCE'S, from its navConfig.ts: Master Data, Knowledge Library,
      // License Intelligence, Application Evaluation, then Settings and the two help
      // leaves last. #5 and #6 slot in between Knowledge Library and Settings when they
      // land — Settings, User Guide and Help & Support stay at the bottom.
      // Renamed from "Master Data" to "Portfolio" (owner instruction, 2026-08-08) to
      // avoid confusion with the "Master Data" term used elsewhere. Route/key follow
      // the label as usual — now `/apr/portfolio` — with every other reference to the
      // old `/apr/master-data` path updated alongside it.
      group('apr', 'Portfolio', ['Applications', 'Cost'], 'storage', {
        Applications: 'inventory_2',
        Cost: 'attach_money',
      }),
      // Knowledge Library — a bucket with the source's two document categories.
      group(
        'apr',
        'Knowledge Library',
        ['Architecture & Process Diagrams', 'Licensing'],
        'folder_open',
        { 'Architecture & Process Diagrams': 'schema', Licensing: 'request_quote' },
      ),
      // License Intelligence — sits between Knowledge Library and Settings, as the
      // source's navConfig has it.
      group(
        'apr',
        'License Intelligence',
        ['Licensing Analysis', 'Licensing Results'],
        'request_quote',
        { 'Licensing Analysis': 'request_quote', 'Licensing Results': 'history' },
      ),
      // Application Evaluation — the source's deepest bucket, and the only one that
      // mixes sub-buckets with plain leaves. Structure, order, labels and icons are
      // navConfig.ts:74-105 verbatim, including the two leaves that sit AFTER the three
      // sub-buckets rather than before them.
      nest(
        'apr',
        'Application Evaluation',
        [
          group(
            'apr',
            'APR Model & Execution',
            ['APR Model', 'APR Execution', 'Rationalization Models'],
            'model_training',
            {
              'APR Model': 'model_training',
              'APR Execution': 'play_circle',
              'Rationalization Models': 'rule',
            },
          ),
          group(
            'apr',
            'Initial Disposition',
            ['Application Portfolio Q&A', 'Initial Portfolio Scoring', 'Data Export & Reports'],
            'assignment',
            {
              'Application Portfolio Q&A': 'quiz',
              'Initial Portfolio Scoring': 'analytics',
              'Data Export & Reports': 'download',
            },
          ),
          group(
            'apr',
            'Disposition Intelligence',
            ['Run Analysis', 'Analysis Results', 'Analysis Repository', 'Report Library'],
            'psychology',
            {
              'Run Analysis': 'auto_awesome',
              'Analysis Results': 'insights',
              'Analysis Repository': 'inventory_2',
              'Report Library': 'menu_book',
            },
          ),
          ...sections('apr', ['Final Disposition', 'Audit Trail'], {
            'Final Disposition': 'verified',
            'Audit Trail': 'manage_search',
          }),
        ],
        'fact_check',
      ),
      // Executive Dashboard — NEW WORK, commissioned by the owner, not a port. It sits
      // immediately after Application Evaluation because it reads that bucket's output:
      // dispositions and scores, against Portfolio's cost and License Intelligence's
      // savings. A leaf rather than a bucket — it is one screen with its own tabs.
      //
      // §1.5 lists "Executive Dashboard" among the do-not-port modules because the PLATFORM
      // has one for delivery. This is a different thing: an APR-scoped view of applications,
      // cost and licensing. Recorded in docs/DEVIATIONS.md.
      ...sections('apr', ['Executive Dashboard'], { 'Executive Dashboard': 'insights' }),
      // Settings — the source calls this bucket "Assessment Framework" and nests it
      // under a Settings hub; icons are its navConfig's. It also STARTS THE BOTTOM
      // GROUP: the source pins Settings, User Guide and Help & Support to the foot of
      // the panel behind a spacer and a divider (Sidebar.tsx:110).
      startsBottom(
        group(
          'apr',
          'Settings',
          ['Frameworks', 'Framework Builder', 'Framework Designer', 'Disposition Matrix'],
          'settings',
          {
            Frameworks: 'dashboard',
            'Framework Builder': 'construction',
            'Framework Designer': 'dashboard_customize',
            'Disposition Matrix': 'grid_view',
          },
        ),
      ),
      // Two leaves, not a bucket — the source has them at the very bottom of its nav.
      ...sections('apr', ['User Guide', 'Help & Support'], {
        'User Guide': 'menu_book',
        'Help & Support': 'help_outline',
      }),
    ],
    schemas: ['apr'],
  },

  // --- Sold, not yet built -------------------------------------------------
  // Licensable today, inert until their screens land. Give one its sections and it
  // becomes navigable; nothing else has to change.
  {
    id: 'spn',
    name: 'Strategic Portfolio Navigator',
    // The product's full name, not an abbreviation (owner's ruling, 2026-08-04). The
    // artifacts this file otherwise quotes verbatim had shortened it to fit a narrower
    // drawer; the drawer here has the room.
    navLabel: 'Strategic Portfolio Navigator',
    group: 'applications',
    description: 'Strategic intelligence, discovery and portfolio scoring.',
    icon: 'spn',
    path: '/spn',
    // Navigable from 2026-08-04: Strategic Intelligence → Discoveries is being ported.
    // `status` stays 'planned' until the rest lands — it drives the "Coming soon" LABEL
    // only, never whether the tile is on.
    // No longer 'planned': Discoveries and Transfer are built and in use, and a
    // "Coming soon" chip on a module a client is running discoveries in reads as a mistake.
    status: 'available',
    // FLAT: Discoveries is a top-level entry, not nested under Strategic Intelligence
    // (owner's ruling, 2026-08-04).
    //
    // The source groups it under a Strategic Intelligence landing page of boxed cards —
    // Discoveries, Scoring Framework, Scoring, Costing, Optimization. With only
    // Discoveries ported, reproducing that grouping means a bucket that exists solely to
    // hold one child: an extra click and an extra breadcrumb level that carry no
    // information. If the sibling groups arrive later and the grouping earns its keep,
    // the bucket comes back by wrapping these in `group()` again.
    // Transfer sits beside Discoveries rather than under it: you discover in one place and
    // hand over in another, and the handover spans every discovery rather than belonging to
    // any one of them.
    sections: sections('spn', ['Discoveries', 'Transfer'], {
      Discoveries: 'travel_explore',
      // The act, not its consequence — an arrow leaving a box. `lock` is registered too if
      // the padlock reads better once it is on screen beside Discoveries.
      Transfer: 'outbound',
    }),
    schemas: ['spn'],
  },
  {
    // Added 2026-08-03, so it post-dates the published artifacts this file otherwise
    // quotes verbatim — name and description are the owner's own wording.
    //
    // Its own product, not a part of App Rationalization: rationalization decides what
    // happens to the applications you already run, and this decides which opportunities
    // and projects are worth funding. The second consumes the first's output but is not
    // contained by it.
    //
    // NAME AND ORDER (owner's ruling, 2026-11-01): renamed from Inventory Registry to
    // Opportunity Registry, and listed directly after Strategic Portfolio Navigator. The
    // launcher and the drawer both render straight from this array.
    id: 'portfolio_definition',
    name: 'Opportunity Registry',
    navLabel: 'Opportunity Registry',
    group: 'applications',
    description: 'Define opportunities and projects, calculate benefits, and prioritize.',
    icon: 'portfolio_definition',
    path: '/portfolio_definition',
    // Navigable from 2026-08-06: cloned from `ai_readiness_template` per the owner's
    // brief. `status` drives the "Coming soon" LABEL only, never whether the tile opens.
    status: 'available',
    // STRUCTURE AND LABELS ARE THE SOURCE'S, verbatim, in the ORDER THE OWNER SPECIFIED
    // (2026-08-05). Only the menu DESIGN is this platform's — an icon per entry and the
    // light-blue selected state — which is exactly the one thing his brief said to change.
    //
    // TWO DELIBERATE DEPARTURES FROM THE SOURCE'S OWN TREE, both his instructions:
    //
    //   1. PROGRAM QUICK START IS STANDALONE AND FIRST. In the source it is buried as a
    //      child of Strategic Intelligence (layout-v2.ejs:73). His words: "in this
    //      application, the program Quickstart should be a standalone very first menu
    //      after the start menu."
    //
    //   2. SETTINGS CARRIES FOUR BRANCHES — Assessment Framework, Business Process
    //      Definition, Opportunity Attributes and Project Execution. The source's Settings
    //      also holds User Management, Optimal AI Platform and Token Usage. Those are
    //      absent rather than stubbed: User Management and Token Usage are platform-level
    //      (CLAUDE.md §1.5), and Optimal AI Platform belongs to AI Readiness and is
    //      already ported there.
    //
    // There is no 'Start menu' section: the module path itself renders the start screen,
    // the same way APR and SPN do. A nav row pointing at the page you are already on
    // would be the odd one out here, not the norm.
    sections: [
      ...sections('portfolio_definition', ['Program Quick Start', 'Quick Start Analysis'], {
        'Program Quick Start': 'play_circle',
        /* Its own entry beside the intake screen — §5 moved the proposal review out of
           Program Quick Start, which shrank by ~410 lines, because the review could not be
           filtered while it shared a screen with the upload form. */
        'Quick Start Analysis': 'manage_search',
      }),
      // PROJECT ADMINISTRATION IS GONE FROM HERE, replaced by Settings -> Project
      // Execution below (owner, 2026-08-08). Its screen file is untouched and its route
      // mapping still resolves; only the menu entry has been withdrawn.
      group(
        'portfolio_definition',
        'Projects',
        ['Project Registry', 'Dashboard'],
        'folder_open',
        {
          'Project Registry': 'storage',
          Dashboard: 'dashboard',
        },
      ),
      group(
        'portfolio_definition',
        'Opportunities',
        [
          'Opportunity Registry',
          'Scoring',
          'Scoring Queue',
          'In Progress',
          'Completed',
          /* Renamed from 'Dashboard' on the owner's instruction (2026-09-30): it reports on
             scoring, and "Dashboard" beside four scoring screens said nothing about which
             dashboard it was. The key, and therefore the route, becomes scoring-dashboard;
             the old path is redirected in PortfolioDefinitionModule. */
          'Scoring Dashboard',
          // LAST, by the owner's instruction (2026-08-10): "add another menu that should be
          // the very last one that should be audit trail".
          'Audit Trail',
        ],
        'lightbulb',
        {
          'Opportunity Registry': 'storage',
          Scoring: 'quiz',
          'Scoring Queue': 'assignment',
          'In Progress': 'history',
          Completed: 'fact_check',
          'Scoring Dashboard': 'dashboard',
          /*
           * `manage_search` — a magnifier over a document. Reads as "inspect the record",
           * which is what an audit trail is for.
           *
           * NOT `history`: In Progress already owns it two rows above, and two identical
           * glyphs in one menu is how a nav stops being scannable. NOT `history_edu`
           * either — its graduation cap says "course", not "audit".
           */
          'Audit Trail': 'manage_search',
        },
      ),
      ...sections('portfolio_definition', ['Delivery Pathway'], {
        'Delivery Pathway': 'alt_route',
      }),
      // Mixed depth: two leaves beside two buckets, which is why this is `nest` and not
      // `group` — the same shape APR's Application Evaluation needed.
      nest(
        'portfolio_definition',
        'Financial Modeling',
        [
          ...sections('portfolio_definition', ['Scenario Setup', 'Configuration'], {
            'Scenario Setup': 'schema',
            Configuration: 'settings',
          }),
          group(
            'portfolio_definition',
            'MVP Value Model',
            ['Cost & Scenarios', 'Financial Summary', 'Version Comparison'],
            'request_quote',
            {
              'Cost & Scenarios': 'attach_money',
              'Financial Summary': 'analytics',
              'Version Comparison': 'grid_view',
            },
          ),
          group(
            'portfolio_definition',
            'NPV Value Model',
            ['NPV Cost & Scenarios', 'NPV Calculator', 'Scenario Comparison'],
            'insights',
            {
              'NPV Cost & Scenarios': 'attach_money',
              'NPV Calculator': 'analytics',
              'Scenario Comparison': 'grid_view',
            },
          ),
        ],
        'account_balance',
      ),
      group(
        'portfolio_definition',
        'Portfolio Optimizer',
        ['Strategy and Constraints', 'Saved Scenarios', 'Analytics & Visuals'],
        'tune',
        {
          'Strategy and Constraints': 'rule',
          'Saved Scenarios': 'storage',
          'Analytics & Visuals': 'insights',
        },
      ),
      /*
        EXECUTIVE DASHBOARD — a LEAF among the groups, the same shape Project Management's
        uses. One screen, and it reads ACROSS every project and opportunity rather than
        within one, so filing it under a group scoped to a single kind of work would
        misrepresent it. A direct child of the module puts it at the group-header indent,
        which is where a cross-cutting screen belongs.

        LAST before the bottom group, on the owner's instruction (2026-09-30): after
        Portfolio Optimizer. The optimizer is where you choose a portfolio; this is where you
        read what the portfolio is worth.
      */
      ...sections('portfolio_definition', ['Executive Dashboard'], {
        'Executive Dashboard': 'query_stats',
      }),

      startsBottom(
        nest(
        'portfolio_definition',
        'Settings',
        [
          group(
            'portfolio_definition',
            'Assessment Framework',
            ['Frameworks', 'Framework Designer', 'Framework Builder', 'Recommendation'],
            'schema',
            {
              Frameworks: 'grid_view',
              'Framework Designer': 'dashboard_customize',
              'Framework Builder': 'construction',
              Recommendation: 'verified',
            },
          ),
          group(
            'portfolio_definition',
            'Business Process Definition',
            // The nav's own spelling uses an EN DASH; the page heading uses hyphens.
            // Both are the source's and neither is normalised.
            ['L1–L3 Process Hierarchy'],
            'storage',
            { 'L1–L3 Process Hierarchy': 'schema' },
          ),
          /*
            ONE LEAF, NOT A GROUP OF FOUR. Business Value Criteria, Strategic Alignment,
            Benefit / Cost Effectiveness and Delivery Risk are tabs on a single screen now
            (owner, 2026-08-09, against an approved prototype). Four nav rows for four
            near-identical scales was the thing he wanted gone.
          */
          ...sections('portfolio_definition', ['Opportunity Attributes'], {
            'Opportunity Attributes': 'rule',
          }),
          /*
            PROJECT EXECUTION — the source's own Settings branch (layout-v2.ejs:169-171),
            its two leaves in its order. It stands where Project Administration used to be
            in this module's menu, on the owner's instruction (2026-08-08).

            THE SCREENS ARE NOT NEW. Both were cloned during the Project Management port
            and are reached here rather than copied: two copies of a screen drift, and the
            data they configure is one set of tables. The same branch therefore ALSO
            appears under Project Management -> Settings; it was not named for removal, so
            it stays there until the owner says otherwise.
          */
          group(
            'portfolio_definition',
            'Project Execution',
            /* 'Screen Permissions' removed on the owner's instruction, 2026-08-21: too
               much for this stage and adding no value yet. The SCREEN and its server
               routes are intact and unrouted — this is a menu removal, not a deletion, so
               restoring it is one line here plus one in the module's route table. */
            ['Project Visibility'],
            'rule',
            { 'Project Visibility': 'lock' },
          ),
        ],
        'settings',
        ),
      ),
      /*
        SETTINGS OPENS THE BOTTOM GROUP, above User Guide — the owner's ruling
        (2026-08-08), and the shape APR already uses: Settings → User Guide → Help &
        Support. It used to sit up in the main list with the bottom group starting at
        User Guide, which put Settings among the working screens instead of with the
        other chrome.
      */
      ...sections('portfolio_definition', ['User Guide', 'Help & Support'], {
        'User Guide': 'menu_book',
        'Help & Support': 'help_outline',
      }),
    ],
    schemas: ['portfolio_definition'],
  },
  {
    // ORDER: after Strategic Portfolio Navigator (owner's ruling, 2026-08-04). The
    // launcher and the drawer both render straight from this array, so position here is
    // the only place that decides it.
    id: 'ai_readiness',
    name: 'AI Readiness',
    navLabel: 'AI Readiness',
    group: 'applications',
    description: 'Assess AI readiness, frameworks and opportunity fit.',
    icon: 'ai_readiness',
    path: '/ai_readiness',
    // Navigable from 2026-08-04: Strategic Intelligence and Settings → Optimal AI
    // Platform are being ported. `status` stays 'planned' until the rest of the product
    // lands — it drives the "Coming soon" LABEL only, never whether the tile is on.
    // Strategic Intelligence and the AI Platform Selection screens are built.
    status: 'available',
    // STRUCTURE CLONED FROM THE SOURCE'S layout-v2.ejs (owner's ruling, 2026-08-04),
    // including the order and the labels, which are verbatim.
    //
    // Only the branches in scope are populated. Settings carries AI Platform Selection and
    // NOTHING else: the source's Settings also holds Assessment Framework, Business
    // Process Definition, Opportunity Attributes, Project Execution and Token Usage, and
    // those are deliberately absent rather than stubbed, so the tree never offers a
    // screen that does not exist.
    sections: [
      group(
        'ai_readiness',
        'Strategic Intelligence',
        /* 'Program Quick Start' removed on the owner's instruction, 2026-08-17. It is
           AI Readiness's own entry and had no route behind it. Inventory Registry's
           Program Quick Start is a DIFFERENT menu — routed, in use, and untouched. */
        /* 'Platform Fitment Criteria' MOVED to Settings → AI Platform Selection on the
           owner's instruction, 2026-08-17 — it is configuration, and it now carries the
           selection criteria that used to sit in Settings under their own entry. */
        [
          'AI Platforms Catalog',
          'Platform Analysis',
          /* RENAMED from 'AI Platform Fit', owner-directed 2026-08-17. `keyOf` slugifies the
             label into the URL, so the screen also moves to
             /ai_readiness/strategic-intelligence/ai-platform-fitness-analysis, with NO
             redirect from the old path per the same day's standing instruction. The
             Settings → AI Platform Selection entry below is a DIFFERENT menu and keeps its
             own name — only the Strategic Intelligence one was named. */
          'AI Platform Fitness Analysis',
          'Platform Fit Summary',
        ],
        'psychology',
        {
          'AI Platforms Catalog': 'inventory_2',
          'Platform Analysis': 'auto_awesome',
          'AI Platform Fitness Analysis': 'analytics',
          'Platform Fit Summary': 'insights',
        },
      ),
      /*
        SETTINGS OPENS THE BOTTOM GROUP here too — the owner's instruction of 2026-08-11:
        "the settings in AI readiness should be also positioned all the way down and have
        user guide and help and support. Use APR module for both of these as a reference."
      */
      startsBottom(
        nest(
          'ai_readiness',
          'Settings',
          [
            group(
              'ai_readiness',
              /* RENAMED from 'Optimal AI Platform', owner-directed 2026-08-17. The label
                 is what `keyOf` slugifies into the URL segment, so this also moves the
                 screens to /ai_readiness/settings/ai-platform-selection/… — deliberately,
                 and with NO redirect from the old path: "I don't want any reference to any
                 old URL." */
              'AI Platform Selection',
              /* 'AI Platform Selection Criteria' REMOVED and 'Platform Fitment Criteria'
                 moved in, owner-directed 2026-08-17. The criteria that screen edited are
                 now the Platform Capabilities tab of Platform Fitment Criteria, so keeping
                 both would have offered the same list under two names. */
              /* 'AI Platform Fit' REMOVED on the owner's instruction, 2026-08-17. It had
                 no route behind it — it rendered in the drawer and did nothing when
                 clicked — and it predates this session's work. The live screen of that
                 name is Strategic Intelligence → AI Platform Fitness Analysis; this entry
                 was a second, dead reference to it. */
              ['Platform Fitment Criteria'],
              'tune',
              {
                'Platform Fitment Criteria': 'rule',
              },
            ),
          ],
          'settings',
        ),
      ),
      ...sections('ai_readiness', ['User Guide', 'Help & Support'], {
        'User Guide': 'menu_book',
        'Help & Support': 'help_outline',
      }),
    ],
    schemas: ['ai_readiness'],
  },
  {
    /*
     * ORDER: immediately after AI Readiness (owner's instruction, 2026-10-04). The launcher
     * and the drawer both render straight from this array, so position here is the only
     * place that decides it.
     *
     * NEW WORK, not a port — there is no source app for it. Development starts on
     * `feat/erp-functional-area-consolidation`; `status: 'planned'` is what puts the
     * "Coming soon" chip on the tile, and it is honest until the first screen lands. The
     * chip does not grey the tile out or stop it opening (owner's ruling, 2026-08-03):
     * ON or OFF is the licence's business alone.
     *
     * `sections: NO_SECTIONS` means the drawer shows the product with no rows yet, rather
     * than offering screens that do not exist. Sections get added as screens are built.
     */
    id: 'erp_consolidation',
    name: 'ERP / Functional Area Consolidation',
    navLabel: 'ERP Consolidation',
    group: 'applications',
    description: 'Consolidate ERP across IT infrastructure, and/or functional areas across business units and processes.',
    icon: 'erp_consolidation',
    path: '/erp_consolidation',
    /*
     * AVAILABLE — the owner's instruction, 2026-10-05: "you need to remove 'Coming Soon'".
     *
     * It carried 'planned' on the argument that the chip should stay until the module could
     * carry a consolidation end to end. He has overruled that, and the tile now matches what a
     * user actually finds: six working screens — initiatives, business units, the weighted
     * capability model, the assessment grid, the scores with their four quadrants, and the
     * scoring definition. A product people can open and use does not advertise itself as
     * forthcoming.
     *
     * The chip never gated anything either way (owner's ruling, 2026-08-03): ON or OFF is the
     * licence's business alone.
     */
    status: 'available',
    /*
     * THE ROWS ARRIVE WITH THE SCREENS, which is what this entry said they would do while it
     * carried NO_SECTIONS: a nav row for a screen that does not exist is a dead link offered to
     * a user. These three exist and are routed. The rest land as their phases do.
     */
    sections: sections(
      'erp_consolidation',
      [
        /*
         * BUSINESS UNITS LEADS — owner, 2026-10-07: *"Business Unit menu should be positioned
         * above 'initiative' menu"*.
         *
         * NOTE FOR WHOEVER READS THIS NEXT: the module's landing screen is NOT this order's
         * first row any more. `/erp_consolidation` used to redirect to `sections[0]`, so this
         * reorder would have moved the landing to Business Units as a side effect — and the
         * owner's instruction on 2026-10-05 was to land on Initiatives. Two instructions, both
         * standing, so `ErpConsolidationModule.tsx` now names the landing section explicitly.
         * Changing this array no longer changes where the module opens.
         */
        'Business Units',
        'Initiatives',
        'Capability Model',
        'Capability Templates',
        /*
         * KNOWLEDGE LIBRARY — D-025, the owner: *"bring the knowledge Library screen I mention"*
         * (APR's Architecture & Process Diagrams), then *"Move Knowledge Library above Assessment
         * Runs menu"* (2026-10-09).
         */
        'Knowledge Library',
        /*
         * ASSESSMENT RUNS sits between the model and the grid because that is the order the work
         * happens in: the model says what you are judging against, a RUN says which systems one
         * business unit uses for one functional area, and the grid is where the two meet and the
         * percentages are recorded.
         *
         * It was the missing link — the server had the endpoints from Phase 2 and no screen ever
         * called them, so no assessment unit could be created and the grid could only show an
         * empty state. `0107` then made a run a named, versioned record, which is what the owner
         * asked for: *"save the versions of these assessment grids"*.
         */
        'Assessment Runs',
        'Assessment Grid',
        /*
         * SCORING DEFINITION BEFORE SCORES — owner, 2026-10-09: *"Scoring definition screen
         * should come before Scores screen."* The definition is what every score is calculated
         * from, so it is set before the scores are read.
         */
        'Scoring Definition',
        'Scores',
      ],
      {
        'Business Units': 'account_tree',
        Initiatives: 'rocket_launch',
        'Capability Model': 'checklist',
        'Capability Templates': 'schema',
        'Assessment Runs': 'dns',
        'Assessment Grid': 'grid_on',
        Scores: 'leaderboard',
        'Scoring Definition': 'tune',
        /* APR's own icon for its Knowledge Library group (`catalog.ts`, the apr entry). */
        'Knowledge Library': 'folder_open',
      },
    ),
    schemas: ['erp_consolidation'],
  },
  {
    id: 'program_planning',
    name: 'Program Planning & Roadmap',
    navLabel: 'Program Planning',
    group: 'applications',
    description: 'Roadmaps, resource estimates, NPV and program reporting.',
    icon: 'program_planning',
    path: '/program_planning',
    /*
     * AVAILABLE — the owner's instruction, 2026-10-19: "just remove 'Coming soon' from the
     * Welcome screen", ahead of building the module from parts of APR_Template. `status`
     * drives the chip only (owner's ruling, 2026-08-03); whether the tile opens is the
     * licence's business alone.
     */
    status: 'available',
    /*
     * apr_template's "Planning & Integration" hub, promoted to a product of its own. Owner
     * ruling 2026-10-19 (docs/program-planning/PLAN.md D1): the hub itself does not exist
     * here — its four groups ARE the top-level menus, in the source's order, with the
     * Executive Dashboard last. Labels and icons are the source's navConfig.ts:110-153.
     *
     * Applications arrive from App Rationalization's Final Disposition → "Transfer to
     * Integration"; Integration App Master Data is where they appear (D2).
     */
    sections: [
      group(
        'program_planning',
        'Grouping & Scope',
        [
          'Integration App Master Data',
          'Integration Groupings',
          'Phase Effort Planning',
          'Application Dependencies',
        ],
        'workspaces',
        {
          'Integration App Master Data': 'inventory_2',
          'Integration Groupings': 'workspaces',
          'Phase Effort Planning': 'engineering',
          'Application Dependencies': 'share',
        },
      ),
      group(
        'program_planning',
        'Resource Planning',
        ['Resource Skillsets', 'Resource Capacity', 'Resource Allocation', 'Role Utilization'],
        'groups',
        {
          'Resource Skillsets': 'badge',
          'Resource Capacity': 'groups',
          'Resource Allocation': 'assignment_ind',
          'Role Utilization': 'analytics',
        },
      ),
      group(
        'program_planning',
        'Roadmap & Planning',
        // Owner ruling 2026-11-03: Roadmap Approval is removed from the menu — work is
        // approved as entered. The screen and its route stay (see docs/DEVIATIONS.md).
        ['Roadmap Schedule', 'High-Level Implementation Roadmap'],
        'event',
        {
          'Roadmap Schedule': 'event',
          'High-Level Implementation Roadmap': 'timeline',
        },
      ),
      // Owner ruling 2026-11-03: the Business Case group (Disposition Cost Analysis,
      // Benefit Realization) is removed — menu, screens and API. See docs/DEVIATIONS.md.
      ...sections('program_planning', ['Executive Dashboard'], {
        'Executive Dashboard': 'dashboard',
      }),
    ],
    schemas: ['program_planning'],
  },
  {
    id: 'project_management',
    name: 'Project Execution & Project Management',
    navLabel: 'Project Management',
    group: 'applications',
    description: 'Deliverables, milestones, RACI, actions and status reporting.',
    icon: 'project_management',
    path: '/project_management',
    // Navigable from 2026-08-06: cloned from `ai_readiness_template` per the owner's brief,
    // the same way Inventory Registry was.
    status: 'available',
    // STRUCTURE AND LABELS ARE THE SOURCE'S, verbatim — four groups and sixteen leaves,
    // in the source's own order (layout-v2.ejs:114-147). Only the menu DESIGN is this
    // platform's: an icon per entry and the light-blue selected state.
    //
    // ONE DEPARTURE, and it is the owner's ruling (2026-08-06): the source's Program
    // Management bucket opens with a ROADMAP DEFINITION group (Roadmap Definition,
    // Timeline Finalization). His words: "leave those Roadmap definition and other items
    // you listed above. Those will go into another module." So the group is absent HERE —
    // it is not deleted anywhere. Its two screen keys are still carried in
    // `executionScreens.ts`, because `delivery_project_screen_permissions.screen_key`
    // stores them and dropping an entry would orphan existing permission rows.
    //
    // There is no 'Start menu' section: the module path itself renders the start screen,
    // as APR, SPN and Inventory Registry all do.
    sections: [
      /*
        ROADMAP DEFINITION OPENS THE MODULE — the owner's instruction of 2026-08-11: it is the
        first thing you do with a delivery programme, so it sits above Budget & Resources and
        carries the source's own label.

        This SUPERSEDES the 2026-08-06 ruling recorded above, which deferred the group to
        "another module". That module turned out to be this one.

        Both leaves, in the source's own order — Roadmap Definition groups opportunities into
        delivery projects, Timeline Finalization then locks the timeline.
      */
      /*
        ── THE SOURCE'S OWN NAVIGATION, CLONED ────────────────────────────────────────────
        `ai_readiness_template/views/layout-v2.ejs:136-172`, structure and labels verbatim.

        The source's own comment explains the split, and it is worth keeping because it is the
        organising idea rather than a layout preference:

            "Scope splits at the top level: Portfolio is everything ABOVE a single project,
             Program Management is everything WITHIN one. Below that, lifecycle order."

        An earlier pass here built a half-version of this from the build document's illustrative
        snippet — Portfolio with two children, and the rest left flat under invented groups such
        as "Deployment Tracking", which the source does not have. This is the real thing.

        WHAT IS NOT COPIED, and why:

        · `roles` / `badge:'ROLE'` on Program Management. The source gates that whole bucket to
          Administrator and Strategic Analyst. This platform gates nothing in this module today,
          and adding a parent gate is the exact move §14.1.2 warns about — a filtered parent
          takes its children with it. Gating belongs on the leaves, and it is a deliberate
          access change rather than a navigation one, so it is not smuggled in with a reshuffle.

        · Execution Dashboard IS here now, cloned from `views/execution-dashboard.ejs`. It was
          the one screen in §16.1's tree this platform did not carry.
      */
      nest(
        'project_management',
        'Portfolio',
        [
          group(
            'project_management',
            'Project Setup',
            ['Project Creation', 'Project Timeline Finalization'],
            'edit_note',
            { 'Project Creation': 'workspaces', 'Project Timeline Finalization': 'event' },
          ),
          group(
            'project_management',
            'Portfolio Oversight',
            /* Execution Dashboard FIRST — §16.1. It carries NO role gate: §16.2 is explicit
               that lifting `roles` to the Portfolio node is what makes it vanish for everyone
               outside those two roles, and it is the one screen here open to all. */
            ['Execution Dashboard', 'Portfolio Exceptions'],
            'travel_explore',
            { 'Execution Dashboard': 'insights', 'Portfolio Exceptions': 'crisis_alert' },
          ),
        ],
        'account_tree',
      ),
      nest(
        'project_management',
        'Program Management',
        [
          group(
            'project_management',
            'Scope & Ownership',
            [
              'Stream Definition',
              'Work Item Definition',
              'Agile Delivery Workspace',
              'Work Item Assignment',
              'RACI Definition',
            ],
            'assignment',
            {
              'Stream Definition': 'alt_route',
              'Work Item Definition': 'fact_check',
              'Agile Delivery Workspace': 'rocket_launch',
              'Work Item Assignment': 'how_to_reg',
              'RACI Definition': 'grid_view',
            },
          ),
          group(
            'project_management',
            'Budget & Resources',
            ['Budget Plan', 'Budget Tracker', 'Resource Plan', 'Resource Tracker'],
            'attach_money',
            {
              'Budget Plan': 'account_balance',
              'Budget Tracker': 'price_check',
              'Resource Plan': 'groups',
              'Resource Tracker': 'timeline',
            },
          ),
          /*
            ACTION NEEDED — a LEAF among the groups, not a group of its own.

            The screen spans every project, so filing it under a group scoped to one kind of
            work would misrepresent it. Being a direct child of the nest puts it at the
            group-header indent: a lone link at the CHILD indent would read as an orphaned
            child of the group above it.

            Position is deliberate — after the planning groups (Scope & Ownership, Budget &
            Resources) and before the execution ones. It is the hinge between planning work
            and running it.
          */
          ...sections('project_management', ['Action Needed'], {
            'Action Needed': 'notification_important',
          }),

          /*
            DELIVERY EXECUTION'S ORDER IS THE OWNER'S, NOT THE SOURCE'S.

            The source reads Execution Control Center → Action Register → Meeting Intelligence
            (`layout-v2.ejs:163-166`). The owner's instruction (2026-08-12) is that both Meeting
            Intelligence and Action Register come BEFORE the Control Center, which is the order
            the work actually happens in: a meeting produces actions, and the Control Center is
            where you then see the effect.

            This is the one place this module's nav departs from §16.1, deliberately and on
            instruction. Recorded in docs/DEVIATIONS.md. Routes are unchanged — only the reading
            order moves, so nothing needs a redirect.
          */
          group(
            'project_management',
            'Delivery Execution',
            /* Activity Timeline leads the group, as it does in the source's layout-v2.ejs:163.
               The owner's ruling — Meeting Intelligence and Action Register BEFORE Execution
               Control Center — is untouched: ECC stays last. */
            ['Activity Timeline', 'Meeting Intelligence', 'Action Register', 'Execution Control Center'],
            'monitoring',
            {
              'Activity Timeline': 'history',
              'Meeting Intelligence': 'record_voice_over',
              'Action Register': 'checklist',
              'Execution Control Center': 'dashboard_customize',
            },
          ),
          group(
            'project_management',
            'Status Reporting',
            ['Weekly Status Meetings', 'SteerCo Deck Creation', 'Report Templates'],
            'insights',
            {
              // The source's own ligatures for these two screens (`_DECK_CFG`).
              'Weekly Status Meetings': 'event_note',
              'SteerCo Deck Creation': 'slideshow',
              'Report Templates': 'description',
            },
          ),
          /*
            EXECUTIVE DASHBOARD — a LEAF among the groups, like Action Needed above.

            One screen, so it is not a group; and it reads ACROSS programs rather than
            within one, so filing it under a group scoped to a single kind of work would
            misrepresent it. A direct child of the nest puts it at the group-header indent,
            which is where a cross-cutting screen belongs.

            Position is the owner's (2026-09-30): between Status Reporting and Closeout &
            Audit. Status reporting is what you send out; this is what you read before you
            send it.
          */
          ...sections('project_management', ['Executive Dashboard'], {
            'Executive Dashboard': 'query_stats',
          }),

          group(
            'project_management',
            'Closeout & Audit',
            ['Project Completion', 'Audit Trail'],
            'task_alt',
            { 'Project Completion': 'task_alt', 'Audit Trail': 'manage_search' },
          ),
        ],
        'rocket_launch',
      ),
      // Settings carries ONE branch, Project Execution — the owner's brief for piece 2 is
      // "Settings → Project Execution only". The source's Settings also holds User
      // Management, Assessment Framework, Optimal AI Platform and Token Usage; those are
      // absent HERE rather than removed anywhere: User Management and Token Usage are
      // platform-level (CLAUDE.md §1.5), and Assessment Framework already lives under
      // Inventory Registry's own Settings, where it was ported.
      /*
        SETTINGS OPENS THE BOTTOM GROUP, above User Guide — the owner's instruction
        (2026-08-11: "put settings all the way down to the end of the menu"), and the shape
        APR and Inventory Registry already use: Settings -> User Guide -> Help & Support.
        It used to sit among the working screens, which put configuration in the middle of
        the delivery flow.
      */
      startsBottom(
        nest(
          'project_management',
          'Settings',
          [
            group(
              'project_management',
              'Project Execution',
              /* 'Screen Permissions' removed on the owner's instruction, 2026-08-21: too
               much for this stage and adding no value yet. The SCREEN and its server
               routes are intact and unrouted — this is a menu removal, not a deletion, so
               restoring it is one line here plus one in the module's route table. */
            ['Project Visibility'],
              'rule',
              { 'Project Visibility': 'lock' },
            ),
          ],
          'settings',
        ),
      ),
      // Two leaves, not a bucket — the same treatment APR gives them at the foot of its nav.
      ...sections('project_management', ['User Guide', 'Help & Support'], {
        'User Guide': 'menu_book',
        'Help & Support': 'help_outline',
      }),
    ],
    schemas: ['project_management'],
  },
  {
    // RE-PURPOSED, not renamed-in-passing (owner's ruling, 2026-08-06). This slot was
    // Strategic Intelligence; that product is not being built, and the tile is re-used
    // for RFx Desk — the hub for RFQ, RFI and RFP processes.
    //
    // The `id` and `path` deliberately still read `strategic_intelligence`. Changing them
    // would orphan this install's `platform.app_entitlements` row (which is keyed by
    // module_key and currently carries 10 seats) and would need a migration to move.
    // Nothing reaches the path today because the module is `planned` with no sections, so
    // its tile does not open — the identifier is invisible to anyone using the product.
    // Worth renaming properly if and when RFx Desk gets screens; not worth a migration to
    // rename a string nobody sees.
    id: 'strategic_intelligence',
    name: 'RFx Desk',
    navLabel: 'RFx Desk',
    group: 'applications',
    description: 'One hub for RFQ, RFI and RFP processes — from request through response.',
    icon: 'request_quote',
    path: '/strategic_intelligence',
    status: 'planned',
    sections: NO_SECTIONS,
    schemas: [],
  },

  {
    // Its own bucket: Entitlements & Licensing lives here, not under Administration.
    // Token Usage and Database Usage also appear under Administration, rendered from
    // one shared definition so they are the same screens, not copies.
    id: 'licensing_usage',
    name: 'Licensing & Usage',
    navLabel: 'Licensing & Usage',
    group: 'talogent',
    description: 'Licensed modules, seat allocation and platform consumption.',
    icon: 'licensing_usage',
    path: '/licensing_usage',
    status: 'available',
    // No Users section here on purpose: Talogent staff create the client's accounts
    // through the CLIENT's own Administration module, not a vendor-side copy of it.
    sections: sections('licensing_usage', [
      'Entitlements & Licensing',
      // The only place talogent.com accounts are listed. Customer accounts live in
      // the customer's own Administration > Users; the two never mix.
      'Talogent Users',
      'Token Usage',
      'Database Usage',
    ]),
    schemas: [],
  },
  {
    id: 'admin',
    name: 'Administration & Configuration',
    navLabel: 'Administration',
    group: 'platform',
    description: 'Users, roles, identity and platform settings.',
    icon: 'admin',
    path: '/admin',
    status: 'available',
    sections: sections('admin', [
      'Company Profile',
      'Users',
      // The customer's own view of what they have and what is left. A separate
      // screen from Talogent's Entitlements & Licensing — the customer never
      // reaches the vendor's.
      'License Usage',
      'SSO / Identity',
      // The navigation artifact had a single 'Configuration' section. On the owner's
      // instruction its parts are standalone sections with their own screens, so
      // nothing is stacked onto a shared page.
      'Notifications',
      // Was 'Integrations' — a placeholder screen with no storage behind it. Renamed
      // on the owner's instruction (2026-10-07): "integrations should be replaced
      // menu with OpenAI integration", then "rename AI Integration into Open AI
      // Integration", and then (2026-10-07, same day) "I believe it is called Open AI
      // and not OpenAI" — so the feature is TWO WORDS. HIS WORDING, verbatim, per the
      // rule at the top of this file, even though the screen configures four providers
      // and the COMPANY OpenAI is one of them. That provider keeps its own spelling in
      // the screen's own copy; only the FEATURE is "Open AI Integration".
      //
      // `keyOf` derives the key from the label, so the route moved with it twice:
      // /admin/integrations -> /admin/openai-integration -> /admin/open-ai-integration.
      // Nothing persisted any of those keys — `platform.user_screen_permissions` holds
      // no admin section rows — so the rename is a label change and nothing else. The
      // admin-only gate on this section lives in `visibility.ts` KEYED BY THAT KEY, and
      // had to move with it; a stale key there would have silently dropped the gate.
      'Open AI Integration',
      'Token Usage',
      'Database Usage',
      // Not in the navigation artifact, but administration-and-configuration.md
      // requires the audit log be queryable in the admin UI.
      'Audit Log',
    ]),
    // Users, audit and install config all live in the `platform` schema.
    schemas: [],
  },
];

/**
 * The shared schema. Everything cross-cutting — users, audit, entitlements, company
 * profile — lives here rather than belonging to one application.
 */
export const SHARED_SCHEMA = 'platform';

/** Navigation group headings, in the order they appear. */
export const MODULE_GROUP_LABELS: Record<ModuleGroup, string> = {
  applications: 'Applications',
  platform: 'Platform',
  talogent: 'Talogent',
};

export const MODULE_GROUP_ORDER: readonly ModuleGroup[] = ['applications', 'platform', 'talogent'];

/**
 * Maps a database schema back to the module that owns it.
 *
 * This is the connector for per-application storage reporting. Returns null for a
 * schema no module claims, so an unmapped schema surfaces as unattributed rather than
 * silently vanishing from the totals — which is what the schemas of the removed
 * modules now do, correctly, since they hold nothing.
 */
export function moduleForSchema(schema: string): ModuleDefinition | null {
  return MODULE_CATALOG.find((m) => m.schemas.includes(schema)) ?? null;
}

const BY_ID = new Map<ModuleId, ModuleDefinition>(MODULE_CATALOG.map((m) => [m.id, m]));

export function getModuleDefinition(id: ModuleId): ModuleDefinition | undefined {
  return BY_ID.get(id);
}

export function isKnownModuleId(value: string): value is ModuleId {
  return BY_ID.has(value as ModuleId);
}
